import { CartLinesUpdateEvent } from '@shopify/events';

// This module is loaded once, even if the reward snippet is re-rendered.
let adding = false;

function showStatus(form, message) {
  if (!form.isConnected) return;
  let status = form.querySelector('[data-upsell-status]');
  if (!status) {
    status = document.createElement('span');
    status.dataset.upsellStatus = '';
    status.setAttribute('role', 'status');
    status.style.cssText = 'display:block;font-size:11px;line-height:1.4;margin-top:5px;max-width:160px;white-space:normal';
    form.append(status);
  }
  status.textContent = message;
}

document.addEventListener('submit', async event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('thauya-cart-rewards .cr-add-form')) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  if (adding) return;

  const button = form.querySelector('button[type="submit"]');
  const payload = new FormData(form);
  const variantId = String(payload.get('id') || '');
  const quantity = Number(payload.get('quantity') || 1);
  if (!/^\d+$/.test(variantId) || !Number.isInteger(quantity) || quantity <= 0) {
    showStatus(form, 'Please choose a valid product and quantity.');
    return;
  }

  adding = true;
  const label = button?.textContent || '+ Add';
  const buttons = [...document.querySelectorAll('thauya-cart-rewards .cr-add-form button[type="submit"]')];
  const disabledStates = buttons.map(item => item.disabled);
  buttons.forEach(item => { item.disabled = true; });
  if (button) {
    button.textContent = 'Adding…';
    button.setAttribute('aria-busy', 'true');
  }
  showStatus(form, '');

  const deferred = CartLinesUpdateEvent.createPromise();
  // Consumers handle this rejection too; this prevents an unhandled rejection
  // when no cart consumer is present.
  deferred.promise.catch(() => {});
  let confirmedAdded = false;
  let sent = false;

  try {
    const root = window.Shopify?.routes?.root || '/';
    const sections = [...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map(component => component.dataset.sectionId)
        .filter(Boolean)
    )].slice(0, 5);

    payload.delete('return_to');
    payload.delete('sections');
    payload.delete('sections_url');
    if (sections.length) payload.set('sections', sections.join(','));
    payload.set('sections_url', location.pathname);

    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise
    }));

    sent = true;
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: payload
    });
    const added = await response.json();
    if (!response.ok || added.status) {
      sent = false; // A parsed rejection confirms this request was refused.
      throw new Error(added.description || added.message || 'This product could not be added.');
    }
    confirmedAdded = true;

    const cartResponse = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!cartResponse.ok) throw new Error('Cart refresh failed.');
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-upsell',
        sourceId: 'cart-upsell',
        itemCount: quantity,
        sections: added.sections,
        didError: false
      }
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
    showStatus(form, 'Added to cart.');
  } catch (error) {
    deferred.reject(error);
    const message = confirmedAdded
      ? 'Added, but the cart could not refresh. Refresh the page; do not add it again.'
      : sent
        ? 'Could not confirm the addition. Check or refresh your cart before trying again.'
        : error.message || 'Could not add this product. Please try again.';
    showStatus(form, message);
    console.warn('Cart upsell:', message, error);
  } finally {
    adding = false;
    buttons.forEach((item, index) => {
      if (item.isConnected) item.disabled = disabledStates[index];
    });
    if (button?.isConnected) {
      button.textContent = label;
      button.removeAttribute('aria-busy');
    }
  }
}, true);
